// 手牌視圖（單一座位的暗牌 + 副露 + 花牌呈現）
// 對應 PRD §4.4 CGameScene 版面、§3.2.3 UI 元件
//
// === 使用圖片 ===
// 本元件不直接 load.image，所有牌的視覺都透過 CTileSprite 渲染。
// 實際 image asset 對應請見 src/ui/tile-sprite.ts 檔頭的 ===使用圖片=== 區。
// 此處只決定：哪個座位 → 用哪一個 TileFace（CTileSprite 再依 face 選圖 + 旋轉）。
//
// 一個 CHandView 對應一家：
// - 玩家自家：牌面朝上、可互動（點選）、可高亮（TileFace.HandBottom）
// - 對家：倒立牌背橫排（TileFace.HandTop）
// - 左/右家：直立牌背直排（TileFace.HandLeft / TileFace.HandRight）
// - 副露：依座位選 TileFace.Meld{Bottom|Top|Left|Right}（內部旋轉 0/180/±90°）
//
// 設計原則：
// - 純 props 渲染：接 hand 物件 + 座位方位 + 是否自家 + 選取的 tile id
// - update() 接收新 hand 重繪（簡化：destroy old + 重建 sprites）
// - 點擊事件透過 onTileClick 回呼向外傳遞，CHandView 不持互動狀態

import { isMeldKindVisible } from '@core/meld-visibility';
import Phaser from 'phaser';
import type { THand } from '@core/hand';
import type { TTile } from '@core/tile';
import { bindHandTileActivation } from '@input/hand-tile-activation';
import { findHandTileAtPoint } from '@input/hand-tile-hit-test';
import { buildPlayerHandReflowPlan } from '@input/player-discard-motion';
import { drawnTileInitialAlpha } from './drawn-tile-appearance';
import {
  TILE_BODY_HEIGHT,
  TILE_BODY_WIDTH,
  CTileSprite,
  TileFace,
  type TTileFace,
} from './tile-sprite';
import { UIDepth } from './ui-depths';
import {
  meldDirectionOf,
  flowerScaleOf,
  tilePositionAt,
  meldPositionAt,
  kongStackOffsetFor,
  flowerSlotPositionAt,
  drawnSeparatedPositionAt,
  revealedHandLayoutOf,
  selfDrawHandTuningOf,
  selfDrawnTilePositionAt,
  tabledHandLayoutOf,
  type ISelfDrawHandTuning,
  type THandPosition,
  type TFlowerGrowDirection,
  type TMeldDirection,
  type TMeldKongStackDirection,
} from './hand-layout';

// 方位 / 方向 const 與型別、佈局數學已移到 hand-layout.ts（Phaser-free，可單元測試）；
// 此處 re-export const 與型別，維持 `@ui/index` → hand-view 既有對外介面不變。
export {
  HandPosition,
  FlowerGrowDirection,
  MeldDirection,
  MeldKongStackDirection,
} from './hand-layout';
export type {
  THandPosition,
  TFlowerGrowDirection,
  TMeldDirection,
  TMeldKongStackDirection,
  ISelfDrawHandTuning,
} from './hand-layout';

// 自家有選中牌時，整列暗牌套用的淡化倍率（與 baseAlpha 相乘）
// 與 game-scene 的 SELECTED_HAND_DIM_ALPHA 同義，分開定義以維持兩邊獨立
const SELECTED_HAND_DIM_ALPHA = 0.5;

// 補花動畫：飛行中的花 sprite depth（蓋過手牌 / 副露 / 海底，確保飛行可見）
const FLOWER_FLYER_DEPTH = UIDepth.FlowerFlyer;

// 本家摸牌動畫：剛摸進、停在風字旁的牌 depth（蓋過風字徽章與手牌，確保醒目）
const DRAW_FLYER_DEPTH = UIDepth.DrawFlyer;

const DRAWN_TILE_FADE_IN_MS = 250; // 摸進牌從透明淡入的時長

// flowerFirstOnTop=true 時，第一張花的 depth；後續每張遞減 1（仍 > 0，保持在牌面之上）
const FLOWER_STACK_TOP_DEPTH = UIDepth.HandFlowerStackTop;

export type THandViewConfig = {
  /** 座位方位 */
  readonly position: THandPosition;
  /** 是否為玩家自家（牌面朝上、可互動） */
  readonly isSelf: boolean;
  /** 暗牌起點座標（左上角；left/right 時為頂端） */
  readonly originX: number;
  readonly originY: number;
  /** 暗牌間距（px，未經 scale；step = tileGap × scale） */
  readonly tileGap: number;
  /** 暗牌縮放 */
  readonly scale: number;
  // ---- 暗牌邊緣 anchor（4 向；以「面對牌桌的玩家視角」為基準）----
  // 同方向只能擇一設定；都不設定時 fallback 用 originX/originY 從中心算。
  /**
   * Horizontal left-anchored：leftmost tile 的左邊緣固定在此 X。
   * 用於自家：玩家視角左 = 螢幕左，新增暗牌往右排。
   */
  readonly concealedLeftEdgeX?: number;
  /**
   * Horizontal right-anchored：rightmost tile 的右邊緣固定在此 X。
   * 用於對家：玩家視角左 = 螢幕右，新增暗牌往左排。
   */
  readonly concealedRightEdgeX?: number;
  /**
   * Vertical top-anchored：topmost tile 的上邊緣固定在此 Y。
   * 用於左家：玩家視角左 = 螢幕上，新增暗牌往下排。
   */
  readonly concealedTopEdgeY?: number;
  /**
   * Vertical bottom-anchored：bottommost tile 的下邊緣固定在此 Y。
   * 用於右家：玩家視角左 = 螢幕下，新增暗牌往上排。
   */
  readonly concealedBottomEdgeY?: number;
  /**
   * 暗牌單張寬度（at scale=1），給 horizontal anchor 算 halfWidth 用。
   * 預設 TILE_BODY_WIDTH（90）；對家手牌（hand-top，tile-back-up 63px）可帶 63 校準對齊。
   */
  readonly concealedTileWidth?: number;
  /**
   * 暗牌單張高度（at scale=1），給 vertical anchor 算 halfHeight 用。
   * 預設 TILE_BODY_HEIGHT（144）；左右家手牌（hand-left/right，tile-side-up 99px）可帶 99。
   */
  readonly concealedTileHeight?: number;
  // ---- 副露獨立佈局（可選；未指定時 fallback 到暗牌設定，meld 接在 concealed 後同方向） ----
  /** 副露左側起點 X（left-anchored；新增的牌往右排，舊牌固定在左邊） */
  readonly meldOriginX?: number;
  readonly meldOriginY?: number;
  /** 副露牌間距（未經 meldScale） */
  readonly meldTileGap?: number;
  /** 副露縮放 */
  readonly meldScale?: number;
  /**
   * 副露排列方向（覆寫 concealed 軸向）。
   * 例：左右家手牌是垂直排，副露想橫向延伸到中央就傳 'horizontal'。
   */
  readonly meldDirection?: TMeldDirection;
  /**
   * Horizontal right-anchored：meldDirection='horizontal' 時，rightmost tile 右緣固定在此 X。
   * 新增副露時舊副露往左滑、最右那張牌的右緣不動。
   */
  readonly meldRightEdgeX?: number;
  /**
   * Horizontal left-anchored：meldDirection='horizontal' 時，leftmost tile 左緣固定在此 X。
   * 新增副露時舊副露不動、新副露往右排。優先於 meldOriginX（meldOriginX 是 center，這個是 left edge）。
   * 用於對家副露要靠左側 UI 對齊的情境。
   */
  readonly meldLeftEdgeX?: number;
  /**
   * Vertical bottom-anchored：meldDirection='vertical' 時，bottommost tile 下緣固定在此 Y。
   * 新增副露時舊副露往上滑、最下那張牌的下緣不動。
   * 用於左家：玩家視角右 = 螢幕下。
   */
  readonly meldBottomEdgeY?: number;
  /**
   * Vertical top-anchored：meldDirection='vertical' 時，topmost tile 上緣固定在此 Y。
   * 新增副露時舊副露不動、新副露往下排。
   * 用於右家：玩家視角右 = 螢幕上。
   */
  readonly meldTopEdgeY?: number;
  /**
   * 副露單張牌寬度（at scale=1）。
   * - 水平方向 right-anchored 用來算 halfWidth
   * - 垂直方向「凸」字第 4 張往側邊疊用
   */
  readonly meldTileWidth?: number;
  /**
   * 副露單張牌高度（at scale=1）。
   * - 垂直方向 bottom-anchored 用來算 halfHeight
   * - 水平方向「凸」字第 4 張往上/下疊用
   */
  readonly meldTileHeight?: number;
  /**
   * 槓（4 張）的第 4 張往哪個方向疊，形成「凸」字型。
   * 預設：horizontal → 'up'、vertical → 'left'。
   * 各家可獨立指定（例如上家用 'down'，視覺凸字朝向螢幕中央）。
   */
  readonly meldKongStackDirection?: TMeldKongStackDirection;
  /**
   * 槓的第 4 張往 meldKongStackDirection 方向位移的距離（post-scale px）。
   * 預設用 meldTileWidth (left/right) 或 meldTileHeight (up/down) × meldScale → 完整一張牌 flush 疊。
   * 給「不完整堆疊／小凸出」用：例如自家想讓 4th 只露出一點，設 26 即可。
   */
  readonly meldKongStackOffset?: number;
  /**
   * 整家牌（暗牌 + 副露）的透明度，0~1（0 = 透明、1 = 不透明）。
   * 用途：開發時調低可看到底層 / 重疊區域，方便微調座標；上線值保持 1。
   * 預設 1（不透明）。
   */
  readonly alpha?: number;
  /**
   * 相鄰兩組副露之間的間距（px，未經 meldScale；與 meldTileGap 同單位）。
   * 預設為 meldTileGap（= 1 個 tile-gap 寬，視覺上等於 1 張牌的位置）。
   * 大於 meldTileGap 會拉開、小於則擠近、0 = 緊鄰。
   */
  readonly meldGroupGap?: number;
  // ---- 花牌區（可選；補花後的花公開展示，face-up，沿用該座位副露朝向）----
  // 只用 origin + direction 的簡易排版（花最多 8 張、不需 edge-anchor）。
  // flowerOriginX / flowerOriginY 任一未設 → 不渲染花牌。
  /** 花牌第一張的中心 X */
  readonly flowerOriginX?: number;
  /** 花牌第一張的中心 Y */
  readonly flowerOriginY?: number;
  /** 花牌間距（px，未經 flowerScale；預設 fallback meldTileGap → tileGap） */
  readonly flowerTileGap?: number;
  /** 花牌縮放（預設 fallback meldScale → scale） */
  readonly flowerScale?: number;
  /** 花牌生長方向（第一張＝玩家視角左，往視角右延伸的螢幕方向；預設依 meld 方向推導） */
  readonly flowerGrowDirection?: TFlowerGrowDirection;
  /**
   * 花牌互相重疊時的疊序：true = 第一張 depth 最高、依序遞減（前面那張壓住後面）；
   * false / 預設 = 依繪製順序（後面那張壓住前面）。直排重疊（如下家）需設 true。
   */
  readonly flowerFirstOnTop?: boolean;
  /** 點擊回呼（只在 isSelf=true 時觸發） */
  readonly onTileClick?: (tile: TTile) => void;
};

/**
 * CHandView — 一家手牌的視覺呈現。
 *
 * 內部維護 CTileSprite 陣列；update() 時會清掉重建（MVP 簡化做法）。
 * 之後要做平滑動畫（補花、抓進浮起）可改為 diff-based 更新。
 */
export class CHandView {
  private readonly tileSprites: CTileSprite[] = [];
  private readonly meldSprites: CTileSprite[] = [];
  private readonly flowerSprites: CTileSprite[] = [];
  // 本家摸牌動畫進行中、停在風字旁/飛入手牌的那張牌（不歸 tileSprites 管，避免 update() 清掉）
  private drawFlyer: CTileSprite | null = null;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly config: THandViewConfig
  ) {}

  /**
   * 用最新 THand 狀態重繪手牌。
   * @param hand 當前手牌資料
   * @param selectedTileId 玩家當前選取要打的牌 id；自家有選取時，只「那一張」套用淡化 alpha
   * @param options.showBorders 是否在每張可選暗牌上畫黃色邊框（自家、可選且未選中時 = true）
   *   有選中那張時，所有手牌一律不畫邊框；邊框由上方放大預覽承接
   */
  update(
    hand: THand,
    selectedTileId?: number,
    options?: {
      readonly showBorders?: boolean;
      /** 鍵盤／電視操作時只高亮目前焦點牌；未指定則維持原本全部可選牌高亮。 */
      readonly focusedTileId?: number;
      /** 把這張暗牌渲染成「隱形佔位」（保留空位，給出牌升起動畫期間用）。 */
      readonly hiddenTileId?: number;
      /**
       * 桌機 hover 回饋：滑鼠移到可點的暗牌上時顯示外框 + pointer 游標。
       * 由 scene 在「輪到本家出牌 + 桌機」時帶 true（見 supportsHover / canPlayerSelectTile）。
       */
      readonly hoverHighlight?: boolean;
      /**
       * 摸進、尚未決定去留的牌 id：抽出來排在該家玩家視角的手牌右方，
       * 與其餘手牌空一個間隙（四家一致）。
       * 出牌 / 併入後 caller 不再帶此 key → 那張回到 inline 排序位。
       */
      readonly drawnTileId?: number;
      /** 本次新摸進的牌從透明淡入；只在 tile.drawn 的首次視覺更新帶入。 */
      readonly fadeInDrawnTile?: boolean;
      /** 胡牌儀式：把別家的暗牌改成該座位朝向的正面牌，呈現攤牌。 */
      readonly revealConcealed?: boolean;
      /**
       * 自摸儀式：包含 drawnTileId 在內的整副手牌全部推倒、牌面朝上；
       * 胡張仍停在該家玩家視角右方的第 17 張摸進牌分離位置。
       */
      readonly selfDrawReveal?: boolean;
      /** 自摸預覽即時調校：整列位移、牌距與牌尺寸；未傳時使用正式自摸 UI 數值。 */
      readonly selfDrawTuning?: ISelfDrawHandTuning;
    }
  ): void {
    this.clear();

    const selfDrawReveal = options?.selfDrawReveal === true;
    const selfDrawTuning = selfDrawReveal
      ? (options?.selfDrawTuning ?? selfDrawHandTuningOf(this.config))
      : undefined;
    const revealConcealed = selfDrawReveal || options?.revealConcealed === true;
    const concealedFace: TTileFace = selfDrawReveal
      ? this.getMeldFace()
      : revealConcealed
        ? this.getRevealedConcealedFace()
        : this.getConcealedFace();
    const concealedLayout = selfDrawReveal
      ? tabledHandLayoutOf(this.config, selfDrawTuning)
      : revealConcealed
        ? revealedHandLayoutOf(this.config)
        : this.config;
    const meldFace: TTileFace = this.getMeldFace();
    const baseAlpha = this.config.alpha ?? 1;
    const showBorders = options?.showBorders === true;
    const hasSelection = selectedTileId !== undefined;
    const concealedTotal = hand.concealed.length;

    // ---- 副露區（先畫，讓後畫的暗牌蓋過 meld）----
    // 下家 / 上家垂直佈局時副露會與手牌重疊，暗牌須在上層才視覺合理。
    // 槓（4 張）排成「凸」字：base = 3 張排在主軸，第 4 張疊在中間那張的對應側邊
    // 因此槓在主軸上仍只佔 3 個 slot，不會擠壓其他副露的橫向 / 縱向位置
    const meldScale = this.config.meldScale ?? this.config.scale;
    const meldGapPxPreScale = this.config.meldTileGap ?? this.config.tileGap; // 1 slot 的 pre-scale px
    // meldGroupGap 是 px（與 meldTileGap 同單位）；預設 = meldTileGap，等同於 1 個 tile-gap 寬
    const groupGapPx = this.config.meldGroupGap ?? meldGapPxPreScale;
    // 內部 cursor 仍以 slot 為單位（簡化算式），把 px gap 換算成等效 slot 數
    const groupGapInSlots = groupGapPx / meldGapPxPreScale;

    // 計算主軸上的總 slot 數（槓佔 3 不佔 4）以推 lastMeldCursor for end-anchored
    const meldAxisSlotsTotal =
      hand.melds.reduce((sum, m) => sum + Math.min(m.tiles.length, 3), 0) +
      Math.max(0, hand.melds.length - 1) * groupGapInSlots;
    const lastMeldCursor = meldAxisSlotsTotal - 1;

    let meldCursor = 0;
    for (const meld of hand.melds) {
      const isKong = meld.tiles.length === 4;
      const hiddenMeld = !isMeldKindVisible(meld.type, this.config.isSelf);
      const baseTileCount = isKong ? 3 : meld.tiles.length;

      // 1. 排基底（3 張 for kong、3 張 for pong、3 張 for chi）
      const baseStartCursor = meldCursor;
      for (let i = 0; i < baseTileCount; i++) {
        const tile = meld.tiles[i]!;
        const { x, y } = this.getMeldPosition(meldCursor, lastMeldCursor, concealedTotal);
        const sprite = new CTileSprite(this.scene, x, y, tile, meldFace, undefined, hiddenMeld);
        sprite.setScale(meldScale);
        sprite.setAlpha(baseAlpha);
        this.meldSprites.push(sprite);
        meldCursor++;
      }

      // 2. 槓：第 4 張疊在基底中間那張的 stack 側（形成「凸」字）
      if (isKong) {
        const middleCursor = baseStartCursor + Math.floor(baseTileCount / 2); // = baseStart + 1
        const middlePos = this.getMeldPosition(middleCursor, lastMeldCursor, concealedTotal);
        const stackedPos = this.applyKongStackOffset(middlePos, meldScale);
        const fourth = meld.tiles[3]!;
        const stacked = new CTileSprite(
          this.scene,
          stackedPos.x,
          stackedPos.y,
          fourth,
          meldFace,
          undefined,
          hiddenMeld
        );
        stacked.setScale(meldScale);
        stacked.setAlpha(baseAlpha);
        this.meldSprites.push(stacked);
      }

      meldCursor += groupGapInSlots; // 每組 meld 之間相隔 meldGroupGap px（已換算成 slot 數）
    }

    // ---- 暗牌（後畫，蓋在 meld 之上）----
    // drawnTileId 有設定時：把「摸進的牌」抽出來，其餘暗牌正常排，摸的牌另排在成長端 + 空隙。
    const drawnId = options?.drawnTileId;
    const restTiles =
      drawnId !== undefined ? hand.concealed.filter((t) => t.id !== drawnId) : hand.concealed;
    const drawnTile =
      drawnId !== undefined ? hand.concealed.find((t) => t.id === drawnId) : undefined;

    // 建立一張暗牌 sprite（含 alpha / 選取淡化 / 隱形佔位 / 邊框 / 互動），並推入 tileSprites。
    const makeConcealedSprite = (
      tile: TTile,
      x: number,
      y: number,
      face: TTileFace = concealedFace,
      scale: number = concealedLayout.scale
    ): void => {
      const sprite = new CTileSprite(this.scene, x, y, tile, face);
      sprite.setScale(scale);
      // 只有自家、且這張就是選中的那張，才套用淡化 alpha；其餘維持 baseAlpha
      const isSelected = this.config.isSelf && selectedTileId === tile.id;
      const renderedAlpha = isSelected ? baseAlpha * SELECTED_HAND_DIM_ALPHA : baseAlpha;
      const initialAlpha = drawnTileInitialAlpha({
        baseAlpha: renderedAlpha,
        tileId: tile.id,
        ...(drawnId !== undefined ? { drawnTileId: drawnId } : {}),
        fadeInDrawnTile: options?.fadeInDrawnTile === true,
      });
      sprite.setAlpha(initialAlpha);
      if (initialAlpha !== renderedAlpha) {
        this.scene.tweens.add({
          targets: sprite,
          alpha: renderedAlpha,
          duration: DRAWN_TILE_FADE_IN_MS,
          ease: 'Linear',
        });
      }
      // 出牌升起動畫期間：這張在原位「隱形佔位」（保留空位，由升起的放大牌承接視覺）
      if (options?.hiddenTileId === tile.id) sprite.setVisible(false);
      // 邊框：自家、要求顯示、且當前無選中（有選中時邊框讓位給上方預覽）
      const matchesKeyboardFocus =
        options?.focusedTileId === undefined || options.focusedTileId === tile.id;
      if (this.config.isSelf && showBorders && !hasSelection && matchesKeyboardFocus) {
        sprite.setHighlight(true);
      }
      if (this.config.isSelf && this.config.onTileClick) {
        // 點擊區域對齊牌底實際尺寸（PRD §10.3 觸控目標）
        const halfW = TILE_BODY_WIDTH / 2;
        const halfH = TILE_BODY_HEIGHT / 2;
        const enableHover = options?.hoverHighlight === true;
        sprite.setInteractive({
          hitArea: new Phaser.Geom.Rectangle(-halfW, -halfH, TILE_BODY_WIDTH, TILE_BODY_HEIGHT),
          hitAreaCallback: Phaser.Geom.Rectangle.Contains,
          useHandCursor: enableHover, // 桌機輪到本家出牌時 → cursor: pointer
        });
        bindHandTileActivation(sprite, () => this.config.onTileClick?.(tile));
        // 桌機 hover：滑鼠移上去顯示外框（card-frame 黃框），移開隱藏
        if (enableHover) {
          sprite.on('pointerover', () => sprite.setHighlight(true));
          sprite.on('pointerout', () => sprite.setHighlight(false));
        }
      }
      this.tileSprites.push(sprite);
    };

    const restTotal = restTiles.length;
    restTiles.forEach((tile, i) => {
      const { x, y } = this.getTilePosition(i, restTotal, concealedLayout);
      makeConcealedSprite(tile, x, y);
    });
    if (drawnTile) {
      const { x, y } = selfDrawReveal
        ? selfDrawnTilePositionAt(this.config, restTotal, selfDrawTuning)
        : this.getDrawnSeparatedPosition(restTotal);
      makeConcealedSprite(drawnTile, x, y);
    }

    // ---- 花牌區（補花後公開展示；origin + direction 簡易排版）----
    this.renderFlowers(hand, baseAlpha);
  }

  /**
   * 渲染花牌：自 flowerOrigin 起，依 flowerGrowDirection 排成一列，face-up（沿用副露朝向）。
   * 未設定 flowerOrigin 或無花 → 不畫。
   */
  private renderFlowers(hand: THand, baseAlpha: number): void {
    if (this.config.flowerOriginX === undefined || this.config.flowerOriginY === undefined) return;
    if (hand.flowers.length === 0) return;

    const face: TTileFace = this.getMeldFace(); // 花公開、face-up，沿用該座位副露朝向
    const scale = this.getFlowerScale();

    hand.flowers.forEach((tile, i) => {
      const { x, y } = this.getFlowerSlotPosition(i);
      const sprite = new CTileSprite(this.scene, x, y, tile, face);
      sprite.setScale(scale);
      sprite.setAlpha(baseAlpha);
      // 直排重疊家：第一張壓在最上、依序遞減（其餘家維持後者蓋前者的繪製順序）
      if (this.config.flowerFirstOnTop) sprite.setDepth(FLOWER_STACK_TOP_DEPTH - i);
      this.flowerSprites.push(sprite);
    });
  }

  /** 花牌縮放（fallback meldScale → scale）。 */
  private getFlowerScale(): number {
    return flowerScaleOf(this.config);
  }

  /** 供自摸預覽 controller 建立該家調校初始值。 */
  getSelfDrawHandTuning(): ISelfDrawHandTuning {
    return selfDrawHandTuningOf(this.config);
  }

  /**
   * 第 index 張花在花牌區的座標。第一張（index 0）固定在 origin（玩家視角左），
   * 後續沿 flowerGrowDirection（往視角右投射的螢幕方向）逐張遞增。
   */
  private getFlowerSlotPosition(index: number): { x: number; y: number } {
    return flowerSlotPositionAt(this.config, index);
  }

  /** 依 id 找暗牌 sprite（補花動畫淡入替補牌用）。找不到回 null。 */
  private findConcealedSprite(tileId: number): CTileSprite | null {
    return this.tileSprites.find((s) => s.tile.id === tileId) ?? null;
  }

  /**
   * 補花動畫（單張花）：三拍呈現「打出花 → 檯面出現花 → 補進一張牌」。
   *  1. 花從手牌原位升起、飛向花牌區，手牌即時補位（中間態：花已離手、替補未進）
   *  2. 花落定在花牌區（檯面出現花）
   *  3. 短暫停留後，替補牌淡入手牌；八仙過海立即結束時 replacement=null，停在花落定
   *
   * @param afterHand  補完之後的手牌（花已進 flowers；若有替補也已進 concealed）
   * @param flower      被替換掉的花
   * @param replacement 摸進的替補牌；八仙過海立即胡牌時為 null
   * @param timings     各拍時長
   * @param onComplete  全部完成後回呼（caller 接著補下一張 / 換下一家）
   *
   * 注意：呼叫前此 view 必須仍顯示「補花前」狀態（花還在 concealed），才能定位花的原位。
   */
  animateFlowerReplacement(
    afterHand: THand,
    flower: TTile,
    replacement: TTile | null,
    timings: {
      readonly flyMs: number;
      readonly holdMs: number;
      readonly drawMs: number;
    },
    onComplete: () => void
  ): void {
    // 0. 花在手牌中目前的螢幕座標（此 view 仍顯示補花前狀態）
    const from = this.getConcealedTilePosition(flower.id);

    // 1. 中間態：花已離手、替補未進、檯面尚無此花 → 手牌補位
    const intermediate: THand = {
      concealed: replacement
        ? afterHand.concealed.filter((t) => t.id !== replacement.id)
        : afterHand.concealed,
      melds: afterHand.melds,
      flowers: afterHand.flowers.filter((t) => t.id !== flower.id),
    };
    this.update(intermediate);

    // 2. 飛行的花 sprite（face-up，沿用副露朝向）；納入 flowerSprites 管理，
    //    下一次 update() 的 clear() 會一併銷毀（落定後由永久花 sprite 取代，亦防中途中斷殘留）
    const scale = this.getFlowerScale();
    const flowerIndex = Math.max(
      0,
      afterHand.flowers.findIndex((t) => t.id === flower.id)
    );
    const target = this.getFlowerSlotPosition(flowerIndex);
    const startPos = from ?? target;
    const flyer = new CTileSprite(this.scene, startPos.x, startPos.y, flower, this.getMeldFace());
    flyer.setScale(from ? this.config.scale : scale);
    flyer.setDepth(FLOWER_FLYER_DEPTH);
    this.flowerSprites.push(flyer);

    const landAndDraw = (): void => {
      // 3. 檯面出現花（替補仍未進）；此 update 的 clear() 會銷毀飛行 sprite，換成永久花
      this.update({
        concealed: intermediate.concealed,
        melds: afterHand.melds,
        flowers: afterHand.flowers,
      });
      if (!replacement) {
        this.update(afterHand);
        onComplete();
        return;
      }
      // 4. 停留後補進替補（淡入）
      this.scene.time.delayedCall(timings.holdMs, () => {
        this.update(afterHand);
        const rep = this.findConcealedSprite(replacement.id);
        if (!rep) {
          onComplete();
          return;
        }
        const finalAlpha = rep.alpha;
        rep.setAlpha(0);
        this.scene.tweens.add({
          targets: rep,
          alpha: finalAlpha,
          duration: timings.drawMs,
          onComplete: () => onComplete(),
        });
      });
    };

    if (from) {
      this.scene.tweens.add({
        targets: flyer,
        x: target.x,
        y: target.y,
        scale,
        duration: timings.flyMs,
        ease: 'Quad.easeOut',
        onComplete: landAndDraw,
      });
    } else {
      landAndDraw();
    }
  }

  /**
   * 槓的第 4 張往對應方向疊出「凸」字（套用在中間那張的座標上）。
   * 偏移量 = 對應軸的「該方向 tile 尺寸 × meldScale」。
   */
  private applyKongStackOffset(
    middle: { x: number; y: number },
    meldScale: number
  ): { x: number; y: number } {
    return kongStackOffsetFor(this.config, middle, meldScale);
  }

  /** 取得實際使用的 meld 排列方向（沒設定就跟著 position）。 */
  private getMeldDirection(): TMeldDirection {
    return meldDirectionOf(this.config);
  }

  /**
   * 出牌補位動畫：把指定（已隱形佔位）的暗牌移除，其右側暗牌「由左至右」滑動填補空位。
   * 只用於本家（左錨定佈局，新 slot 位置與張數無關）。動畫完成後呼叫 onComplete。
   * 之後 caller 會再 update() 一次，依最新手牌狀態重建（位置與滑動終點一致，不會跳動）。
   */
  collapseGap(tileId: number, durationMs: number, onComplete: () => void): void {
    const idx = this.tileSprites.findIndex((s) => s.tile.id === tileId);
    if (idx === -1) {
      onComplete();
      return;
    }
    // 移除佔位牌（隱形那張）
    const gap = this.tileSprites[idx]!;
    this.tileSprites.splice(idx, 1);
    gap.destroy();

    // 其右側每張滑到「往左一格」的新位置
    const totalAfter = this.tileSprites.length;
    let running = 0;
    for (let i = idx; i < this.tileSprites.length; i++) {
      const sprite = this.tileSprites[i]!;
      const { x, y } = this.getTilePosition(i, totalAfter);
      running++;
      this.scene.tweens.add({
        targets: sprite,
        x,
        y,
        duration: durationMs,
        ease: 'Quad.easeOut',
        onComplete: () => {
          running--;
          if (running === 0) onComplete();
        },
      });
    }
    if (running === 0) onComplete(); // 出的是最右一張，無牌需滑動
  }

  /**
   * 本家摸牌後打出原手牌：從目前「原手牌有缺口 + 摸牌仍分離」的畫面，
   * 直接讓所有留下的牌同步滑到出牌後的最終排序位置。
   *
   * 不建立「先補出牌缺口、再挪出摸牌插槽」的中間態，因此補位、騰位與摸牌滑入
   * 都在同一段 tween 完成。
   */
  animateDiscardAndDrawnMerge(
    afterHand: THand,
    discardedTileId: number,
    durationMs: number,
    onComplete: () => void
  ): void {
    const finalTileIds = afterHand.concealed.map((tile) => tile.id);
    const plan = buildPlayerHandReflowPlan({
      displayedTileIds: this.tileSprites.map((sprite) => sprite.tile.id),
      discardedTileId,
      finalTileIds,
    });
    const targetIndexByTileId = new Map(plan.map((move) => [move.tileId, move.targetIndex]));

    // 打出的隱形佔位牌與任何不屬於最終手牌的殘留 sprite 先移除。
    for (let i = this.tileSprites.length - 1; i >= 0; i--) {
      const sprite = this.tileSprites[i]!;
      if (targetIndexByTileId.has(sprite.tile.id)) continue;
      this.tileSprites.splice(i, 1);
      sprite.destroy();
    }

    // 畫面狀態不完整時直接以最終狀態收尾，避免遺漏牌張。
    if (plan.length !== afterHand.concealed.length) {
      this.update(afterHand);
      onComplete();
      return;
    }

    const moves = plan.map((move) => ({
      sprite: this.findConcealedSprite(move.tileId),
      target: this.getTilePosition(move.targetIndex, afterHand.concealed.length),
    }));
    if (moves.some((move) => move.sprite === null)) {
      this.update(afterHand);
      onComplete();
      return;
    }

    let moving = moves.length;
    for (const move of moves) {
      this.scene.tweens.add({
        targets: move.sprite,
        x: move.target.x,
        y: move.target.y,
        duration: durationMs,
        ease: 'Quad.easeOut',
        onComplete: () => {
          moving--;
          if (moving !== 0) return;
          this.update(afterHand);
          onComplete();
        },
      });
    }

    if (moving === 0) {
      this.update(afterHand);
      onComplete();
    }
  }

  /**
   * 本家摸牌整理動畫（模擬人「摸進一張 → 看一下 → 插進手牌整理」）：
   *  1. 先以「摸牌前」排好（drawnTile 尚未插入）
   *  2. 摸進的牌以放大之姿出現在 fromPos（風字附近，可蓋住風字）
   *  3. 停留 holdMs（看一下這張牌）
   *  4. 計算插入點 → 其右方的牌整個往右挪一格（shiftMs）
   *  5. 摸進的牌移動 + 縮小落到插入點（flyMs）
   *  6. update(afterHand) 還原乾淨狀態，onComplete
   *
   * 只用於本家（bottom，左錨定佈局，slot 位置與張數無關，挪位算式才成立）。
   *
   * @param afterHand   摸牌後完整手牌（drawnTile 已併入 concealed 並排序）
   * @param drawnTile   摸進的牌
   * @param fromPos     摸牌牌出現的起點（螢幕座標，風字附近）
   * @param fromScale   摸牌牌出現時的放大縮放
   * @param timings     各拍時長
   * @param onComplete  完成回呼
   */
  animateDraw(
    afterHand: THand,
    drawnTile: TTile,
    fromPos: { x: number; y: number },
    fromScale: number,
    timings: {
      readonly holdMs: number;
      readonly shiftMs: number;
      readonly flyMs: number;
    },
    onComplete: () => void
  ): void {
    // 防衛：上一張摸牌動畫殘留 flyer 先清掉
    if (this.drawFlyer) {
      this.scene.tweens.killTweensOf(this.drawFlyer);
      this.drawFlyer.destroy();
      this.drawFlyer = null;
    }

    const total = afterHand.concealed.length;
    const targetIndex = afterHand.concealed.findIndex((t) => t.id === drawnTile.id);
    if (targetIndex < 0) {
      // 找不到摸進的牌（不應發生）→ 直接重建收尾
      this.update(afterHand);
      onComplete();
      return;
    }

    // 1. 先排「摸牌前」狀態（把 drawnTile 抽掉）
    const beforeHand: THand = {
      concealed: afterHand.concealed.filter((t) => t.id !== drawnTile.id),
      melds: afterHand.melds,
      flowers: afterHand.flowers,
    };
    this.update(beforeHand);

    // 2. 摸進的牌：放大出現在 fromPos（face-up，高 depth 蓋住風字）
    const flyer = new CTileSprite(
      this.scene,
      fromPos.x,
      fromPos.y,
      drawnTile,
      this.getConcealedFace()
    );
    flyer.setScale(fromScale);
    flyer.setDepth(DRAW_FLYER_DEPTH);
    this.drawFlyer = flyer;

    const target = this.getTilePosition(targetIndex, total);

    const fly = (): void => {
      // 5. 摸進的牌移動 + 縮小落到插入點
      this.scene.tweens.add({
        targets: flyer,
        x: target.x,
        y: target.y,
        scale: this.config.scale,
        duration: timings.flyMs,
        ease: 'Quad.easeOut',
        onComplete: () => {
          this.drawFlyer = null;
          flyer.destroy();
          // 6. 還原乾淨手牌（drawnTile 已在正確位置）
          this.update(afterHand);
          onComplete();
        },
      });
    };

    const shiftThenFly = (): void => {
      // 4. 插入點右方的牌整個往右挪一格（騰出空位）
      let shifting = 0;
      for (let i = targetIndex; i < this.tileSprites.length; i++) {
        const sprite = this.tileSprites[i]!;
        const dest = this.getTilePosition(i + 1, total);
        shifting++;
        this.scene.tweens.add({
          targets: sprite,
          x: dest.x,
          y: dest.y,
          duration: timings.shiftMs,
          ease: 'Quad.easeOut',
          onComplete: () => {
            shifting--;
            if (shifting === 0) fly();
          },
        });
      }
      if (shifting === 0) fly(); // 插入點在最右端 → 無牌需挪動，直接飛入
    };

    // 3. 停留 holdMs（看一下摸進的牌）後開始挪位 + 飛入
    this.scene.time.delayedCall(timings.holdMs, shiftThenFly);
  }

  /** 清掉所有 sprite。 */
  clear(): void {
    for (const s of this.tileSprites) s.destroy();
    for (const s of this.meldSprites) s.destroy();
    for (const s of this.flowerSprites) s.destroy();
    this.tileSprites.length = 0;
    this.meldSprites.length = 0;
    this.flowerSprites.length = 0;
    if (this.drawFlyer) {
      this.scene.tweens.killTweensOf(this.drawFlyer);
      this.drawFlyer.destroy();
      this.drawFlyer = null;
    }
  }

  /**
   * 取得某張暗牌目前在畫面上的座標（給選牌預覽 / 動畫對齊用）。
   * 找不到（牌已不在手上、或 tile id 不存在）回 null。
   */
  getConcealedTilePosition(tileId: number): { x: number; y: number } | null {
    for (const s of this.tileSprites) {
      if (s.tile.id === tileId) return { x: s.x, y: s.y };
    }
    return null;
  }

  /**
   * 依 scene world 座標找本家暗牌。
   * 給 scene 級 pointerdown fallback 使用：即使 Phaser game-object input 在同步重建後漏事件，
   * 仍可直接從目前可見 sprite 的實際位置命中牌。
   */
  getConcealedTileAtPoint(x: number, y: number): TTile | null {
    return findHandTileAtPoint(
      x,
      y,
      this.tileSprites.map((sprite) => ({
        value: sprite.tile,
        x: sprite.x,
        y: sprite.y,
        halfWidth: (TILE_BODY_WIDTH * Math.abs(sprite.scaleX)) / 2,
        halfHeight: (TILE_BODY_HEIGHT * Math.abs(sprite.scaleY)) / 2,
        visible: sprite.active && sprite.visible,
      }))
    );
  }

  /**
   * 依座位選暗牌（手牌）face。
   * - 自家：正面朝玩家
   * - 對家：倒立牌背
   * - 左/右家：直立牌背（排版方向由 getTilePosition 用 position 區分）
   */
  private getConcealedFace(): TTileFace {
    if (this.config.isSelf) return TileFace.HandBottom;
    switch (this.config.position) {
      case 'top':
        return TileFace.HandTop;
      case 'left':
        return TileFace.HandLeft;
      case 'right':
        return TileFace.HandRight;
      case 'bottom':
      default:
        return TileFace.HandBottom;
    }
  }

  /**
   * 依座位選副露（meld）face。
   * - 自家：正面正立
   * - 對家：正面倒立
   * - 左/右家：正面側立（旋轉 ±90°）
   */
  private getMeldFace(): TTileFace {
    switch (this.config.position) {
      case 'top':
        return TileFace.MeldTop;
      case 'left':
        return TileFace.MeldLeft;
      case 'right':
        return TileFace.MeldRight;
      case 'bottom':
      default:
        return TileFace.MeldBottom;
    }
  }

  /**
   * 胡牌攤牌的正面牌朝向。
   * 自家原本就是大張正面牌，維持 HandBottom；其他三家才改用對應方位的公開牌。
   */
  private getRevealedConcealedFace(): TTileFace {
    if (this.config.position === 'bottom') return TileFace.HandBottom;
    return this.getMeldFace();
  }

  /**
   * 計算第 N 張暗牌的座標。
   *
   * 解析順序（edge-anchor 優先；同軸只能擇一設定）：
   * - 水平（bottom/top）：concealedLeftEdgeX | concealedRightEdgeX | originX (中心 fallback)
   * - 垂直（left/right）： concealedTopEdgeY  | concealedBottomEdgeY | originY (中心 fallback)
   *
   * 「Edge」對齊 = 該方向上 first/last tile 的「外緣」固定，新增牌從另一端延伸出去。
   */
  private getTilePosition(
    index: number,
    total: number,
    config: THandViewConfig = this.config
  ): { x: number; y: number } {
    return tilePositionAt(config, index, total);
  }

  /**
   * 摸進的牌「分離」座標：排在手牌成長端（最後一張的下一格）再加一個額外空隙。
   * 成長方向用 index 0→1 的單位向量推得，四個方位（bottom/top/left/right）通用。
   * @param restCount 其餘暗牌數（不含摸的那張）
   */
  private getDrawnSeparatedPosition(restCount: number): {
    x: number;
    y: number;
  } {
    return drawnSeparatedPositionAt(this.config, restCount);
  }

  /**
   * 摸進的牌「併入手牌」動畫：從分離處滑進排序後該在的位置（沿用 animateDraw 的挪位 + 飛入）。
   * 用於本家「摸到的牌沒打出去 → 收進手牌」。fromPos 自動取分離座標、fromScale = 手牌縮放。
   * @param afterHand  併入後完整手牌（drawnTile 已在 concealed 排序位）
   */
  animateDrawnMerge(
    afterHand: THand,
    drawnTile: TTile,
    timings: {
      readonly holdMs: number;
      readonly shiftMs: number;
      readonly flyMs: number;
    },
    onComplete: () => void
  ): void {
    const restTotal = Math.max(0, afterHand.concealed.length - 1);
    const fromPos = this.getDrawnSeparatedPosition(restTotal);
    this.animateDraw(afterHand, drawnTile, fromPos, this.config.scale, timings, onComplete);
  }

  /**
   * 計算第 N 張副露牌的座標。
   *
   * 解析順序（edge-anchored 優先）：
   * 1. Horizontal right-anchored：meldRightEdgeX 設定 + meldDirection='horizontal'（自家用：視角右=螢幕右）
   * 2. Horizontal left-anchored：meldLeftEdgeX 設定 + meldDirection='horizontal'（對家用：視角右=螢幕左）
   * 3. Vertical bottom-anchored：meldBottomEdgeY 設定 + meldDirection='vertical'（左家用：視角右=螢幕下）
   * 4. Vertical top-anchored：meldTopEdgeY 設定 + meldDirection='vertical'（右家用：視角右=螢幕上）
   * 5. Start-anchored：meldOriginX/Y 設定（從 origin 往右/下排，origin = center of first tile）
   * 6. Fallback：接在 concealed 後同方向 + 1 slot 間隔（舊行為）
   */
  private getMeldPosition(
    meldIndex: number,
    lastMeldCursor: number,
    concealedCount: number
  ): { x: number; y: number } {
    return meldPositionAt(this.config, meldIndex, lastMeldCursor, concealedCount);
  }
}
